import { LineChart as RechartsLineChart, Line, XAxis, YAxis, CartesianGrid, ResponsiveContainer } from "recharts";

export type DataPoint = {
  /** ISO date from API (kept for tooltips) */
  date: string;
  /** Preformatted X label produced by the bucketing layer (day, month, or range) */
  label?: string;

  // Users view
  totalUsers?: number;
  registeredUsers?: number;
  guestUsers?: number;
  newRegisteredUsers?: number;
  newGuestUsers?: number;

  // Sessions view (single line)
  value?: number;

  // For custom dot tooltip positioning
  x?: number;
  y?: number;
};

type LineChartProps = {
  data: DataPoint[];
  showMultipleLines?: boolean;
  visibleLines?: Record<string, boolean>;
  onDataPointClick?: (dataPoint: DataPoint) => void;
};

const lineColors: Record<string, string> = {
  totalUsers: "#1C67AD",
  registeredUsers: "#87C38E",
  guestUsers: "#6b7280",
  newRegisteredUsers: "#8b5cf6",
  newGuestUsers: "#E28D93",
};

const CustomDot = (props: any) => {
  const { cx, cy, payload, dataKey, onDataPointClick } = props;
  const dotColor = lineColors[dataKey] || lineColors.totalUsers;

  const handleClick = (e: any) => {
    e.stopPropagation();
    if (onDataPointClick) {
      onDataPointClick({ ...payload, x: cx, y: cy });
    }
  };

  // Keep your special-case rendering if needed
  if (payload.totalUsers === 1728 && dataKey === "totalUsers") {
    return (
      <g onClick={handleClick} style={{ cursor: "pointer" }}>
        <circle cx={cx} cy={cy} r={15} fill="transparent" />
        <circle cx={cx} cy={cy} r={6} fill={dotColor} />
        <rect x={cx - 20} y={cy - 35} width={40} height={20} fill={dotColor} rx={4} />
        <text x={cx} y={cy - 20} textAnchor="middle" fontSize={12} fill="white">
          1728
        </text>
      </g>
    );
  }

  return (
    <g onClick={handleClick} style={{ cursor: "pointer" }}>
      <circle cx={cx} cy={cy} r={15} fill="transparent" />
      <circle cx={cx} cy={cy} r={6} fill={dotColor} />
    </g>
  );
};

export const LineChart = ({ data, showMultipleLines = false, visibleLines = {}, onDataPointClick }: LineChartProps) => {
  const defaultVisibleLines = {
    totalUsers: true,
    registeredUsers: true,
    guestUsers: false,
    newRegisteredUsers: false,
    newGuestUsers: false,
  };

  const activeLines = { ...defaultVisibleLines, ...visibleLines };

  // Dynamic Y-axis domain based on visible series
  const getMaxValue = () => {
    if (!data.length) return 100;

    const values = data.flatMap((item) => {
      const vals: number[] = [];
      if (showMultipleLines) {
        if (activeLines.totalUsers) vals.push(item.totalUsers ?? 0);
        if (activeLines.registeredUsers) vals.push(item.registeredUsers ?? 0);
        if (activeLines.guestUsers) vals.push(item.guestUsers ?? 0);
        if (activeLines.newRegisteredUsers) vals.push(item.newRegisteredUsers ?? 0);
        if (activeLines.newGuestUsers) vals.push(item.newGuestUsers ?? 0);
      } else {
        vals.push(item.value ?? 0);
      }
      return vals;
    });

    const max = Math.max(...values);
    return Math.ceil(max * 1.1); // +10% headroom
  };

  // If you want fewer X ticks, adjust interval based on length. (Does not drop points.)
  const xTickInterval = data.length > 20 ? Math.floor(data.length / 8) : 0;

  return (
    <ResponsiveContainer width="100%" height={350}>
      <RechartsLineChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
        <XAxis
          dataKey="label" // <-- use preformatted label from bucketing
          axisLine={false}
          tickLine={false}
          tick={{ fontSize: 12, fill: "#1C67AD" }}
          interval={xTickInterval}
        />
        <YAxis
          axisLine={false}
          tickLine={false}
          tick={{ fontSize: 12, fill: "#1C67AD" }}
          domain={[0, getMaxValue()]}
          allowDecimals={false}
        />

        {showMultipleLines ? (
          <>
            {activeLines.totalUsers && (
              <Line
                type="monotone"
                dataKey="totalUsers"
                stroke={lineColors.totalUsers}
                strokeWidth={2}
                dot={(props) => <CustomDot {...props} onDataPointClick={onDataPointClick} />}
              />
            )}
            {activeLines.registeredUsers && (
              <Line
                type="monotone"
                dataKey="registeredUsers"
                stroke={lineColors.registeredUsers}
                strokeWidth={2}
                dot={(props) => <CustomDot {...props} onDataPointClick={onDataPointClick} />}
              />
            )}
            {activeLines.guestUsers && (
              <Line
                type="monotone"
                dataKey="guestUsers"
                stroke={lineColors.guestUsers}
                strokeWidth={2}
                dot={(props) => <CustomDot {...props} onDataPointClick={onDataPointClick} />}
              />
            )}
            {activeLines.newRegisteredUsers && (
              <Line
                type="monotone"
                dataKey="newRegisteredUsers"
                stroke={lineColors.newRegisteredUsers}
                strokeWidth={2}
                dot={(props) => <CustomDot {...props} onDataPointClick={onDataPointClick} />}
              />
            )}
            {activeLines.newGuestUsers && (
              <Line
                type="monotone"
                dataKey="newGuestUsers"
                stroke={lineColors.newGuestUsers}
                strokeWidth={2}
                dot={(props) => <CustomDot {...props} onDataPointClick={onDataPointClick} />}
              />
            )}
          </>
        ) : (
          <Line
            type="monotone"
            dataKey="value"
            stroke={lineColors.totalUsers}
            strokeWidth={2}
            dot={(props) => <CustomDot {...props} onDataPointClick={onDataPointClick} />}
          />
        )}
      </RechartsLineChart>
    </ResponsiveContainer>
  );
};
